<template>
  <div class="container">
    <div class="box-title">
      <h4>
        <i class="fal fa-truck-container"></i>
        {{ $t("fleetTripDetail.title") }}
        <span v-if="trip" :style="{color: 'crimson', marginLeft: '8px'}">{{ trip.trip_code }}</span>
        <span v-if="trip" class="status-chip" :style="{background: FleetTripStatusColor(trip.status)}">
          {{ FleetTripStatusLabel(trip.status) }}
        </span>
        <span v-if="trip && trip.direction" class="dir-chip"
              :class="trip.direction === 'return' ? 'dir-return' : 'dir-outbound'">
          <i class="fal" :class="trip.direction === 'return' ? 'fa-undo' : 'fa-truck-loading'"></i>
          {{ trip.direction === 'return' ? $t('fleetTripDetail.direction_return') : $t('fleetTripDetail.direction_outbound') }}
        </span>
        <span class="btn-add">
          <span v-if="trip" class="auto-refresh-control">
            <span class="refresh-label">{{ $t("fleetTripDetail.auto_refresh") }}:</span>
            <span class="refresh-options">
              <button
                  :class="['refresh-btn', {active: autoRefreshInterval === 0}]"
                  @click="SetAutoRefresh(0)"
              >{{ $t("fleetTripDetail.off") }}</button>
              <button
                  :class="['refresh-btn', {active: autoRefreshInterval === 5000}]"
                  @click="SetAutoRefresh(5000)"
              >5s</button>
              <button
                  :class="['refresh-btn', {active: autoRefreshInterval === 10000}]"
                  @click="SetAutoRefresh(10000)"
              >10s</button>
            </span>
            <!-- Manual refresh, in the SAME pill as the interval options: it is
                 the same concern (how this page gets fresh data), so it belongs
                 beside them rather than floating off next to ກັບຄືນ. Silent
                 path — the icon spinning here is the only feedback needed. -->
            <button class="refresh-now" :disabled="manualRefreshing"
                    :title="$t('fleetTripDetail.refresh_now_title')" @click="ManualRefresh()">
              <i class="fal" :class="manualRefreshing ? 'fa-spinner fa-spin' : 'fa-sync'"></i>
            </button>
            <span v-if="autoRefreshInterval > 0 && TripIsLive" class="refresh-indicator">
              <i class="fal fa-sync-alt fa-spin"></i>
            </span>
            <span v-else-if="autoRefreshInterval > 0" class="refresh-paused" :title="$t('fleetTripDetail.paused_title')">
              {{ $t("fleetTripDetail.paused") }}
            </span>
          </span>
          <vs-button flat @click="$router.go(-1)">
            <i class="fal fa-arrow-left" :style="{marginRight: '5px'}"></i> {{ $t("common.previous") }}
          </vs-button>
        </span>
      </h4>
    </div>

    <Loading v-if="tripLoading"/>

    <div v-if="trip && !tripLoading">
      <!-- Header + actions -->
      <div class="box">
        <div class="columns is-multiline">
          <div class="column is-3">
            <p class="meta-label">{{ $t("fleetTripDetail.route") }}</p>
            <p class="meta-value">{{ trip.route_name }}</p>
          </div>
          <div class="column is-2">
            <p class="meta-label">{{ $t("fleetTripDetail.date") }}</p>
            <p class="meta-value">{{ FleetFormatDate(trip.service_date) }}</p>
          </div>
          <div class="column is-1">
            <p class="meta-label">{{ $t("fleetTripDetail.round") }}</p>
            <p class="meta-value">{{ trip.round_number }}</p>
          </div>
          <div class="column is-2">
            <p class="meta-label">{{ $t("fleetTripDetail.truck_plate") }}</p>
            <p class="meta-value" v-if="trip.truck_plate">{{ trip.truck_plate }} <span :style="{color: 'grey', fontSize: '12px'}">{{ trip.truck_type }}</span></p>
            <p class="meta-value" v-else>
              <span class="no-truck-chip" :title="$t('fleetTripDetail.no_truck_title')">{{ $t("fleetTripDetail.no_truck") }}</span>
            </p>
          </div>
          <div class="column is-2">
            <p class="meta-label">{{ $t("fleetTripDetail.planned_departure") }}</p>
            <p class="meta-value">{{ FleetFormatDateTime(trip.planned_departure_at) }}</p>
          </div>
          <div class="column is-2">
            <p class="meta-label">{{ $t("fleetTripDetail.started_closed") }}</p>
            <p class="meta-value" :style="{fontSize: '13px'}">
              {{ FleetFormatDateTime(trip.started_at) }}<br>{{ FleetFormatDateTime(trip.closed_at) }}
            </p>
          </div>
          <div class="column is-3">
            <p class="meta-label">{{ $t("fleetTripDetail.odometer_start_end") }}</p>
            <p class="meta-value">
              {{ trip.start_odometer_km !== null ? FleetFormatNumber(trip.start_odometer_km, 1) : '...' }} /
              {{ trip.end_odometer_km !== null ? FleetFormatNumber(trip.end_odometer_km, 1) : '...' }} km
            </p>
          </div>
          <div class="column is-4">
            <p class="meta-label" :title="$t('fleetTripDetail.on_route_totals_title')">{{ $t("fleetTripDetail.on_route_totals_label") }}</p>
            <p class="meta-value">
              {{ FleetTripTotals(trip).bills || 0 }} /
              {{ FleetTripTotals(trip).pieces || 0 }} /
              {{ FleetFormatNumber(FleetTripTotals(trip).weight_kg || 0, 1) }}
              <span v-if="trip.totals && trip.totals.delivered_pieces !== undefined"
                    :style="{color: '#2dcc70', fontSize: '13px'}">
                · {{ $t("fleetTripDetail.delivered") }} {{ trip.totals.delivered_pieces }}
              </span>
              <span v-if="trip.totals && trip.totals.off_route && trip.totals.off_route.pieces"
                    class="offroute-chip"
                    :title="$t('fleetTripDetail.off_route_header_title')"
                    @click="OpenOffRouteDrill()">
                · {{ $t("fleetTripDetail.off_route") }} {{ trip.totals.off_route.pieces }}
                <span v-if="trip.totals.off_route.on_truck_pieces" :style="{fontSize: '11px'}">
                  ({{ trip.totals.off_route.on_truck_pieces }} {{ $t("fleetTripDetail.on_truck") }})
                </span>
              </span>
            </p>
          </div>
          <div class="column is-5">
            <p class="meta-label">{{ $t("fleetTripDetail.remark") }}</p>
            <p class="meta-value" :style="{fontSize: '13px'}">{{ trip.remark || '...' }}</p>
          </div>
        </div>

        <div class="action-row">
          <vs-button v-if="trip.status === 'planned'" flat success :loading="btnLoading" @click="StartTrip()">
            <i class="fal fa-play" :style="{marginRight: '5px'}"></i> {{ $t("fleetTripDetail.start_trip") }}
          </vs-button>
          <vs-button v-if="trip.status === 'planned' || trip.status === 'in_progress'" flat
                     @click="OpenCloseDialog()">
            <i class="fal fa-flag-checkered" :style="{marginRight: '5px'}"></i> {{ $t("fleetTripDetail.close_trip") }}
          </vs-button>
          <vs-button v-if="trip.status === 'planned' || trip.status === 'in_progress'" flat danger
                     :loading="btnLoading" @click="CancelTrip()">
            <i class="fal fa-ban" :style="{marginRight: '5px'}"></i> {{ $t("fleetTripDetail.cancel_trip") }}
          </vs-button>
          <vs-button v-if="trip.status === 'closed'" flat warn :loading="btnLoading" @click="ReopenTrip()">
            <i class="fal fa-undo" :style="{marginRight: '5px'}"></i> {{ $t("fleetTripDetail.reopen_trip") }}
          </vs-button>
          <vs-button flat :loading="volumeLoading" @click="ToggleVolumePanel()">
            <i class="fal fa-weight-hanging" :style="{marginRight: '5px'}"></i> {{ $t("fleetTripDetail.truck_size_recommendation") }}
          </vs-button>
          <vs-button v-if="trip.status === 'planned'" flat @click="ToggleEditPanel()">
            <i class="fad fa-pencil-alt" :style="{marginRight: '5px'}"></i> {{ $t("fleetTripDetail.edit_trip") }}
          </vs-button>
          <vs-button v-if="trip.direction !== 'return' && !trip.paired_trip_id" flat
                     :loading="btnLoading" :title="$t('fleetTripDetail.make_return_trip_title')" @click="MakeReturnTrip()">
            <i class="fal fa-undo" :style="{marginRight: '5px'}"></i> {{ $t("fleetTripDetail.make_return_trip") }}
          </vs-button>
          <router-link v-if="trip.paired_trip_id" class="paired-link"
                       :to="{name: 'fleet.trips.detail', params: {id: trip.paired_trip_id}}"
                       :title="trip.direction === 'return' ? $t('fleetTripDetail.paired_outbound_title') : $t('fleetTripDetail.paired_return_title')">
            <i class="fal fa-exchange"></i>
            {{ trip.direction === 'return' ? $t('fleetTripDetail.view_outbound_trip') : $t('fleetTripDetail.view_return_trip') }}
            <i class="fal fa-arrow-right" :style="{marginLeft: '2px'}"></i>
          </router-link>
        </div>
      </div>

      <!-- Edit while planned (FR-012): change truck / planned departure -->
      <div class="box" v-if="showEditPanel && trip.status === 'planned'">
        <label class="label"><i class="fad fa-pencil-alt"></i> {{ $t("fleetTripDetail.edit_trip_planned_only") }}</label>
        <div class="columns">
          <div class="column is-5">
            <label class="label">{{ $t("fleetTripDetail.change_truck") }}</label>
            <model-list-select
                :list="trucks"
                v-model="editTruckId"
                :isDisabled="editRemoveTruck"
                :placeholder="$t('fleetTripDetail.select_truck_placeholder')"
                option-text="display_name"
                option-value="id"
            >
            </model-list-select>
            <!-- A round may wait for its truck (provincial plates are known late);
                 removing it is allowed only while planned — it cannot start without one. -->
            <label class="checkbox" v-if="trip.truck_id" :style="{marginTop: '6px', fontSize: '13px'}">
              <input type="checkbox" v-model="editRemoveTruck">
              {{ $t("fleetTripDetail.remove_truck") }}
            </label>
          </div>
          <div class="column is-3">
            <label class="label">{{ $t("fleetTripDetail.departure_time") }}</label>
            <input class="input" type="time" v-model="editDepartureTime">
          </div>
          <div class="column is-2" :style="{display: 'flex', alignItems: 'flex-end'}">
            <vs-button flat :loading="btnLoading" @click="SubmitEditTrip()">{{ $t("fleetTripDetail.save") }}</vs-button>
          </div>
        </div>
      </div>

      <!-- Volume recommendation panel -->
      <div class="box" v-if="showVolumePanel && volumeRecommendation">
        <label class="label"><i class="fal fa-weight-hanging"></i> {{ $t("fleetTripDetail.volume_panel_title") }}</label>
        <p :style="{fontSize: '13px'}" v-if="volumeRecommendation.volume && volumeRecommendation.volume.totals">
          {{ $t("fleetTripDetail.total") }}: {{ volumeRecommendation.volume.totals.bills }} {{ $t("fleetTripDetail.bills") }} /
          {{ volumeRecommendation.volume.totals.pieces }} {{ $t("fleetTripDetail.pieces") }} /
          {{ FleetFormatNumber(volumeRecommendation.volume.totals.weight_kg, 1) }} kg /
          {{ volumeRecommendation.volume.totals.cbm }} m³
        </p>
        <div v-if="volumeRecommendation.recommendation">
          <span class="recommend-chip" v-if="volumeRecommendation.recommendation.recommended_size_class">
            {{ $t("fleetTripDetail.recommended") }}: {{ FleetSizeClassLabel(volumeRecommendation.recommendation.recommended_size_class) }}
          </span>
          <span class="recommend-chip none" v-else>{{ $t("fleetTripDetail.no_fitting_truck") }}</span>
          <table class="mini-table" :style="{marginTop: '8px'}">
            <thead>
            <tr>
              <th>{{ $t("fleetTripDetail.plate") }}</th>
              <th>{{ $t("fleetTripDetail.size") }}</th>
              <th>{{ $t("fleetTripDetail.fits") }}</th>
              <th>{{ $t("fleetTripDetail.utilization_kg") }}</th>
              <th>m³%</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(truck, index) in volumeRecommendation.recommendation.trucks" :key="index">
              <td>{{ truck.plate_number }}</td>
              <td>{{ FleetSizeClassLabel(truck.size_class) }}</td>
              <td>
                <i :class="truck.fits ? 'fal fa-check' : 'fal fa-times'"
                   :style="{color: truck.fits ? '#2dcc70' : 'crimson'}"></i>
              </td>
              <td>{{ truck.utilization_pct && truck.utilization_pct.kg !== null ? truck.utilization_pct.kg + '%' : '...' }}</td>
              <td>{{ truck.utilization_pct && truck.utilization_pct.cbm !== null ? truck.utilization_pct.cbm + '%' : '...' }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Close summary (T035) -->
      <div class="box" v-if="displayCloseSummary">
        <label class="label"><i class="fal fa-clipboard-check"></i> {{ $t("fleetTripDetail.close_summary") }}</label>
        <div v-if="displayCloseWarnings.length" class="conflict-box">
          <p v-for="(warning, index) in displayCloseWarnings" :key="index"
             :style="{color: '#e8a10f', fontWeight: 'bold', fontSize: '13px'}">
            <i class="fal fa-exclamation-triangle"></i> {{ WarningLabel(warning) }}
          </p>
        </div>
        <div class="columns is-multiline" :style="{fontSize: '13px'}">
          <div class="column is-3" v-if="displayCloseSummary.stops">
            <p class="meta-label">{{ $t("fleetTripDetail.stops") }}</p>
            <p>{{ $t("fleetTripDetail.visited") }}: {{ displayCloseSummary.stops.visited }} / {{ displayCloseSummary.stops.total }}
              ({{ $t("fleetTripDetail.skipped") }} {{ displayCloseSummary.stops.skipped }})</p>
          </div>
          <div class="column is-9" v-if="displayCloseSummary.parcels">
            <p class="meta-label">{{ $t("fleetTripDetail.parcels") }}</p>
            <p>
              {{ $t("fleetTripDetail.loaded_on_truck") }} {{ displayCloseSummary.parcels.loaded }} ·
              {{ $t("fleetTripDetail.delivered") }} <span :style="{color: '#2dcc70'}">{{ displayCloseSummary.parcels.delivered }}</span> ·
              {{ $t("fleetTripDetail.wrong_stop_unloaded") }} <span :style="{color: '#e8a10f'}">{{ displayCloseSummary.parcels.wrong_stop }}</span> ·
              <span :title="$t('fleetTripDetail.on_truck_incl_off_route_title')">{{ $t("fleetTripDetail.on_truck_incl_off_route") }}</span>
              <span :style="{color: 'crimson'}">{{ displayCloseSummary.parcels.on_truck }}</span> ·
              {{ $t("fleetTripDetail.transferred_trip") }} {{ displayCloseSummary.parcels.transferred }} ·
              {{ $t("fleetTripDetail.removed") }} {{ displayCloseSummary.parcels.removed }}
            </p>
          </div>
          <div class="column is-12"
               v-if="displayCloseSummary.parcels && displayCloseSummary.parcels.leftover && displayCloseSummary.parcels.leftover.length">
            <p class="meta-label">{{ $t("fleetTripDetail.leftover_on_truck") }}:</p>
            <p v-for="(item, index) in displayCloseSummary.parcels.leftover" :key="index">
              - {{ item.bill_number }}
              <span :style="{color: 'grey'}">({{ FleetStopTypeLabel(item.dest_stop_type) }} #{{ item.dest_stop_id }})</span>
            </p>
          </div>
          <!-- GPS distance cross-check (001-fleet-gps-tracking, US4) -->
          <div class="column is-12" v-if="displayCloseSummary.distance">
            <p class="meta-label">
              {{ $t("fleetTripDetail.gps_distance_vs") }}
              <span :style="{color: DistanceConfidenceColor(displayCloseSummary.distance.gps_confidence), fontWeight: 'bold'}">
                · {{ DistanceConfidenceLabel(displayCloseSummary.distance.gps_confidence) }}
              </span>
            </p>
            <p>
              GPS <span :style="{fontWeight: 'bold'}">{{ NullableKm(displayCloseSummary.distance.gps_km) }}</span> ·
              {{ $t("fleetTripDetail.odometer") }} {{ NullableKm(displayCloseSummary.distance.odometer_km) }} ·
              {{ $t("fleetTripDetail.planned") }} {{ NullableKm(displayCloseSummary.distance.planned_km) }}
            </p>
          </div>
        </div>
      </div>

      <!-- GPS integrity (001-fleet-gps-tracking): honesty + continuity of the
           trail — coverage of the trip window, gaps (app off), OS-confirmed
           fake-GPS fixes, filter rejects. Self-hides on an old backend / GPS
           off / pre-feature trips (gps_integrity absent). Live overlay while
           the trip is in progress; the persisted close-time snapshot after. -->
      <div class="box" v-if="TripGpsIntegrity">
        <label class="label">
          <i class="fal fa-satellite-dish"></i> {{ $t("fleetTripDetail.gps_integrity") }}
          <span v-if="TripGpsIntegrity.live" class="gps-live-chip">{{ $t("fleetTripDetail.gps_live") }}</span>
        </label>
        <div class="columns is-multiline" :style="{fontSize: '13px'}">
          <div class="column is-3">
            <p class="meta-label">{{ $t("fleetTripDetail.gps_coverage") }}</p>
            <p>
              <span :style="{color: FleetGpsCoverageColor(TripGpsIntegrity.coverage_pct), fontWeight: 'bold', fontSize: '18px'}">
                {{ FleetFormatNumber(TripGpsIntegrity.coverage_pct, 1) }}%
              </span>
              {{ $t("fleetTripDetail.of_trip_time") }} {{ FleetFormatDuration(TripGpsIntegrity.window_s) }}
            </p>
          </div>
          <div class="column is-3">
            <p class="meta-label">{{ $t("fleetTripDetail.gps_gaps") }}</p>
            <p>
              {{ TripGpsIntegrity.gap_count }} {{ $t("fleetTripDetail.times_unit") }}
              <template v-if="TripGpsIntegrity.gap_count > 0">
                · {{ $t("fleetTripDetail.longest") }} {{ FleetFormatDuration(TripGpsIntegrity.longest_gap_s) }}
              </template>
            </p>
          </div>
          <div class="column is-3">
            <p class="meta-label">{{ $t("fleetTripDetail.gps_fix_count") }}</p>
            <p>
              {{ FleetFormatNumber(TripGpsIntegrity.total_pings) }} {{ $t("fleetTripDetail.gps_points_unit") }}
              <span v-if="TripGpsIntegrity.rejected_pings > 0" :style="{color: '#e8a10f'}">
                · {{ $t("fleetTripDetail.filtered_out") }} {{ FleetFormatNumber(TripGpsIntegrity.rejected_pings) }}
              </span>
            </p>
          </div>
          <div class="column is-3">
            <p class="meta-label">{{ $t("fleetTripDetail.mocked_fixes") }}</p>
            <p :style="TripGpsIntegrity.mocked_pings > 0 ? {color: '#e21312', fontWeight: 'bold'} : {color: '#2dcc70'}">
              {{ FleetFormatNumber(TripGpsIntegrity.mocked_pings) }} {{ $t("fleetTripDetail.gps_points_unit") }}
            </p>
          </div>
        </div>
        <div v-if="TripGpsFlags.length" class="gps-flags">
          <span v-for="(flag, index) in TripGpsFlags" :key="index"
                class="gps-flag-chip" :style="{background: FleetGpsFlagColor(flag)}">
            <i class="fal fa-exclamation-triangle"></i> {{ FleetGpsFlagLabel(flag) }}
          </span>
        </div>
        <p v-else :style="{color: '#2dcc70', fontSize: '13px'}">
          <i class="fal fa-check-circle"></i> {{ $t("fleetTripDetail.gps_no_anomaly") }}
        </p>
        <p class="gps-note">
          {{ $t("fleetTripDetail.gps_note") }}
        </p>
      </div>

      <!-- Trip route map (same controls as the route builder) -->
      <div class="box" v-if="tripMapStops.length">
        <label class="label"><i class="fal fa-map-marked-alt"></i> {{ $t("fleetTripDetail.trip_route_map") }}</label>
        <FleetStopsMap :stops="tripMapStops" :checkins="tripMapCheckins" :truck="mapTruck"
                       height="420px" @drill="OnMapDrill"/>
        <p :style="{fontSize: '12px', color: 'grey', marginTop: '5px'}">
          <i class="fas fa-circle" :style="{color: '#2dcc70'}"></i> {{ $t("fleetTripDetail.start_point") }}
          <i class="fas fa-circle" :style="{color: '#195BFF', marginLeft: '10px'}"></i> {{ $t("common.ສາຂາ") }}
          <i class="fas fa-circle" :style="{color: '#e8a10f', marginLeft: '10px'}"></i> {{ $t("common.ສູນຄັດແຍກ") }}
          <span v-if="tripMapCheckins.length">
            <i class="fas fa-circle" :style="{color: '#2dcc70', marginLeft: '10px', fontSize: '9px'}"></i>
            {{ $t("fleetTripDetail.driver_checkin_points") }}
          </span>
          <span v-if="liveTruck" class="live-truck-note" :class="{stale: liveTruck.stale}">
            <i class="fal fa-truck"></i>
            {{ liveTruck.stale ? $t('fleetTripDetail.truck_stale') : $t('fleetTripDetail.truck_moving') }}
            · {{ $t("fleetTripDetail.last_seen") }} {{ LiveTruckSeen }}
            <template v-if="liveTruck.speed_kmh !== null && liveTruck.speed_kmh !== undefined">
              · {{ FleetFormatNumber(liveTruck.speed_kmh, 0) }} km/h
            </template>
            <template v-if="autoRefreshInterval === 0">· {{ $t("fleetTripDetail.enable_auto_refresh_hint") }}</template>
          </span>
        </p>
        <p v-if="EtaNote" class="eta-note" :class="{stationary: liveEta.state === 'stationary'}">
          <i class="fal fa-stopwatch"></i> {{ EtaNote }}
        </p>
      </div>

      <!-- Per-stop parcel movement ledger (stop-load-ledger) -->
      <div class="box" v-if="ledger && ledger.stops && ledger.stops.length">
        <label class="label"><i class="fal fa-dolly-flatbed"></i> {{ $t("fleetTripDetail.ledger_title") }}</label>

        <div class="recon-banner" :class="'recon-' + (ledger.reconciliation.state || 'open')">
          <i :class="LedgerStateIcon(ledger.reconciliation.state)"></i>
          <b>{{ LedgerStateLabel(ledger.reconciliation.state) }}</b>
          <span>· {{ $t("fleetTripDetail.still_on_truck_on_route") }} {{ ledger.reconciliation.final_residual_pieces }} {{ $t("fleetTripDetail.pieces") }}</span>
          <span v-if="ledger.reconciliation.off_route_pieces" class="recon-warn"
                :title="$t('fleetTripDetail.off_route_recon_title')">
            · {{ $t("fleetTripDetail.off_route") }} {{ ledger.reconciliation.off_route_pieces }} {{ $t("fleetTripDetail.pieces") }}
            <template v-if="ledger.reconciliation.off_route_on_truck_pieces">({{ ledger.reconciliation.off_route_on_truck_pieces }} {{ $t("fleetTripDetail.still_on_truck_short") }})</template>
          </span>
          <span v-if="ledger.reconciliation.administrative_exit_pieces">
            · {{ $t("fleetTripDetail.cancelled") }} {{ ledger.reconciliation.administrative_exit_pieces }} {{ $t("fleetTripDetail.pieces") }}
          </span>
          <span v-if="ledger.reconciliation.ambiguous_legacy_rows" class="recon-warn">
            · {{ $t("fleetTripDetail.ambiguous_legacy") }} {{ ledger.reconciliation.ambiguous_legacy_rows }}
          </span>
        </div>

        <div class="table-scroll">
          <table class="ledger-table">
            <thead>
            <tr>
              <th>#</th>
              <th>{{ $t("fleetTripDetail.stop") }}</th>
              <th class="num" :title="$t('fleetTripDetail.col_in_title')">{{ $t("fleetTripDetail.col_in") }}</th>
              <th class="num" :title="$t('fleetTripDetail.col_out_title')">{{ $t("fleetTripDetail.col_out") }}</th>
              <th class="num" :title="$t('fleetTripDetail.col_entering_title')">{{ $t("fleetTripDetail.col_entering") }}</th>
              <th class="num" :title="$t('fleetTripDetail.col_leaving_title')">{{ $t("fleetTripDetail.col_leaving") }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="row in ledger.stops" :key="row.trip_stop_id">
              <td :style="{textAlign: 'center'}">{{ row.sequence }}</td>
              <td>
                {{ row.stop_name || (FleetStopTypeLabel(row.stop_type) + ' #' + row.stop_id) }}
                <span v-if="IsOriginStop(row)" class="origin-tag"
                      :title="$t('fleetTripDetail.origin_ledger_title')">
                  <i class="fal fa-truck-loading"></i> {{ $t("fleetTripDetail.origin") }}
                </span>
                <small v-if="isRepeatedFacility(row)" :style="{color: '#e8a10f'}">· {{ $t("fleetTripDetail.visit_no", { n: row.sequence }) }}</small>
              </td>
              <td class="num" :class="{clickable: row.in.pieces}" @click="row.in.pieces && OpenDrill(row, 'in')">
                <b v-if="row.in.pieces" :style="{color: '#2dcc70'}">+{{ row.in.pieces }}</b>
                <span v-else>—</span>
                <small v-if="row.in.pieces"> / {{ row.in.bills }} {{ $t("fleetTripDetail.bills") }}</small>
                <div v-if="row.off_route && row.off_route.loaded_here"
                     :style="{color: '#e8a10f', fontSize: '11px', cursor: 'pointer'}"
                     :title="$t('fleetTripDetail.off_route_loaded_here_title')"
                     @click.stop="OpenOffRouteDrill()">
                  +{{ row.off_route.loaded_here }} {{ $t("fleetTripDetail.off_route") }}
                  <template v-if="row.off_route.on_truck">({{ row.off_route.on_truck }} {{ $t("fleetTripDetail.on_truck_short") }})</template>
                </div>
              </td>
              <td class="num" :class="{clickable: row.out.pieces}" @click="row.out.pieces && OpenDrill(row, 'out')">
                <b v-if="row.out.pieces" :style="{color: '#e2513c'}">−{{ row.out.pieces }}</b>
                <span v-else>—</span>
                <small v-if="row.out.wrong_stop" :style="{color: '#e8a10f'}"> {{ $t("fleetTripDetail.wrong_stop") }} {{ row.out.wrong_stop }}</small>
                <small v-if="row.out.transferred" :style="{color: '#195BFF'}"
                       :title="TransferredToTitle(row.out)">
                  {{ $t("fleetTripDetail.transferred") }} {{ row.out.transferred }}<template v-if="TransferredToPlates(row.out)"> → {{ TransferredToPlates(row.out) }}</template>
                </small>
                <div v-if="row.planned_drop" :style="{color: '#e8a10f', fontSize: '11px'}"
                     :title="$t('fleetTripDetail.planned_drop_title')">
                  {{ $t("fleetTripDetail.planned_drop", { n: row.planned_drop }) }}
                </div>
              </td>
              <td class="num" :style="{color: '#8a909a'}">{{ row.on_board_entering.pieces }}</td>
              <td class="num leaving" :class="{clickable: row.on_board_leaving.pieces}"
                  @click="row.on_board_leaving.pieces && OpenDrill(row, 'on_board_leaving')">
                <b>{{ row.on_board_leaving.pieces }}</b> {{ $t("fleetTripDetail.pieces") }}
                <small v-if="row.on_board_leaving.weight_kg"> / {{ FleetFormatNumber(row.on_board_leaving.weight_kg, 0) }} kg</small>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Drill-in: the parcels behind a clicked movement -->
        <div class="drill-box" v-if="drill.open">
          <div class="drill-head">
            <b>{{ drill.title }}</b>
            <vs-button circle icon flat size="small" @click="CloseDrill()"><i class="fal fa-times"></i></vs-button>
          </div>
          <Loading v-if="drill.loading"/>
          <!-- Off-route bucket drill: where each mis-loaded parcel is headed vs where it is -->
          <table class="mini-table" v-else-if="drill.mode === 'off_route' && drill.rows.length">
            <thead>
            <tr>
              <th>{{ $t("fleetTripDetail.bill_number") }}</th>
              <th>{{ $t("fleetTripDetail.should_go_to") }}</th>
              <th>{{ $t("fleetTripDetail.where_is_it") }}</th>
              <th>kg</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(r, i) in drill.rows" :key="i">
              <td>{{ r.bill_number }}</td>
              <td :style="{color: '#e8a10f'}">
                <span v-if="r.dest_prefix" class="dest-prefix">{{ r.dest_prefix }}</span>
                {{ r.dest_name || (r.dest_stop_id ? ('#' + r.dest_stop_id) : '—') }}
              </td>
              <td :style="{fontSize: '12px'}">
                <span v-if="r.on_truck" :style="{color: 'crimson'}">{{ $t("fleetTripDetail.still_on_truck_loaded_at", { stop: r.loaded_name || r.loaded_stop_id || '—' }) }}</span>
                <span v-else :style="{color: '#2dcc70'}">{{ $t("fleetTripDetail.unloaded_at", { stop: r.delivered_name || r.delivered_stop_id || '—' }) }}</span>
              </td>
              <td>{{ r.weight_kg }}</td>
            </tr>
            </tbody>
          </table>
          <table class="mini-table" v-else-if="drill.rows.length">
            <thead>
            <tr>
              <th>{{ $t("fleetTripDetail.bill_number") }}</th>
              <th>{{ $t("fleetTripDetail.status") }}</th>
              <th>kg</th>
              <th>{{ $t("fleetTripDetail.remark") }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(r, i) in drill.rows" :key="i">
              <td>{{ r.bill_number }}
                <span v-if="r.is_off_route" :style="{color: '#e8a10f', fontSize: '11px'}" :title="$t('fleetTripDetail.off_route_parcel_title')">{{ $t("fleetTripDetail.off_route_paren") }}</span>
              </td>
              <td>{{ MovementReasonLabel(r.movement_reason) }}</td>
              <td>{{ r.weight_kg }}</td>
              <td :style="{fontSize: '11px', color: '#8a909a'}">{{ FleetParcelStatusLabel(r.status) }}</td>
            </tr>
            </tbody>
          </table>
          <p v-else :style="{color: 'grey', fontSize: '13px', padding: '8px'}">{{ $t("fleetTripDetail.no_parcels") }}</p>
        </div>
      </div>

      <!-- Bags loaded by a DC bag-outbound. The manifest stores one row per
           PIECE (so the counts are right); this is the physical unit the staff
           handled, rolled back up. Self-hides when nothing was bag-loaded. -->
      <div class="box" v-if="trip.loaded_bags && trip.loaded_bags.length">
        <label class="label">
          <i class="fal fa-box"></i> {{ $t("fleetTripDetail.loaded_bags") }} ({{ trip.loaded_bags.length }})
          <span :style="{color: 'grey', fontSize: '13px', fontWeight: 'normal'}">
            · {{ $t("fleetTripDetail.bags_unload_hint") }}
          </span>
        </label>
        <div class="bag-chips">
          <div v-for="bag in trip.loaded_bags" :key="bag.bag_id"
               class="bag-chip" :class="{'is-done': !bag.on_truck_pieces}"
               :title="$t('fleetTripDetail.bag_click_title')"
               @click="ShowBagParcels(bag)">
            <i :class="bag.on_truck_pieces ? 'fal fa-box' : 'fal fa-check-circle'"></i>
            <span class="bag-number">{{ bag.bag_number || ('#' + bag.bag_id) }}</span>
            <span class="bag-count">
              {{ bag.on_truck_pieces ? (bag.on_truck_pieces + '/' + bag.pieces) : bag.pieces }} {{ $t("fleetTripDetail.pieces") }}
            </span>
          </div>
        </div>
      </div>

      <!-- Searchable full parcel list on the trip (find a parcel by number) -->
      <div class="box">
        <label class="label">
          <i class="fal fa-search"></i> {{ $t("fleetTripDetail.parcels_in_trip") }}
          <span v-if="parcelBagId" class="bag-filter-pill"
                :title="$t('fleetTripDetail.bag_filter_title')"
                @click="ClearBagFilter()">
            {{ $t("fleetTripDetail.bag") }} {{ parcelBagNumber || parcelBagId }} <i class="fal fa-times"></i>
          </span>
          <span v-if="parcelListTotal !== null" :style="{color: 'grey', fontSize: '13px', fontWeight: 'normal'}">
            · {{ $t("fleetTripDetail.found_items", { n: parcelListTotal }) }}
          </span>
          <vs-button flat size="small" :style="{float: 'right'}" @click="showParcelList = !showParcelList">
            <i :class="showParcelList ? 'fal fa-chevron-up' : 'fal fa-chevron-down'" :style="{marginRight: '4px'}"></i>
            {{ showParcelList ? $t('fleetTripDetail.hide') : $t('fleetTripDetail.show') }}
          </vs-button>
        </label>
        <div v-show="showParcelList">
          <div class="columns">
            <div class="column is-6">
              <input class="input" v-model="parcelSearch" :placeholder="$t('fleetTripDetail.parcel_search_placeholder')"
                     @keyup.enter="SearchTripParcels()">
            </div>
            <div class="column is-4">
              <div class="select" :style="{width: '100%'}">
                <select class="input" v-model="parcelStatusFilter" @change="SearchTripParcels()">
                  <option value="">{{ $t("fleetTripDetail.all_statuses") }}</option>
                  <option value="on_truck">{{ $t("fleetTripDetail.on_truck") }}</option>
                  <option value="delivered">{{ $t("fleetTripDetail.delivered") }}</option>
                  <option value="wrong_stop">{{ $t("fleetTripDetail.wrong_stop") }}</option>
                  <option value="transferred">{{ $t("fleetTripDetail.transferred_other_truck") }}</option>
                  <option value="removed">{{ $t("fleetTripDetail.cancelled") }}</option>
                </select>
              </div>
            </div>
            <div class="column is-2" :style="{display: 'flex', alignItems: 'flex-end'}">
              <vs-button flat :loading="parcelListLoading" @click="SearchTripParcels()">
                <i class="fal fa-search" :style="{marginRight: '5px'}"></i> {{ $t("fleetTripDetail.search") }}
              </vs-button>
            </div>
          </div>

        <div class="table-scroll">
          <table class="ledger-table">
            <thead>
            <tr>
              <th>{{ $t("fleetTripDetail.bill_number") }}</th>
              <th>{{ $t("fleetTripDetail.status") }}</th>
              <th>{{ $t("fleetTripDetail.destination") }}</th>
              <th>{{ $t("fleetTripDetail.loaded_at_col") }}</th>
              <th>{{ $t("fleetTripDetail.unloaded_at_col") }}</th>
              <th class="num">kg</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(p, i) in parcelList" :key="i">
              <td>
                {{ p.bill_number }}
                <span v-if="p.is_off_route" :style="{color: '#e8a10f', fontSize: '11px'}"
                      :title="$t('fleetTripDetail.off_route_dest_title')">{{ $t("fleetTripDetail.off_route_paren") }}</span>
              </td>
              <td>
                {{ MovementReasonLabel(p.status) }}
                <span v-if="p.status === 'transferred' && (p.moved_to_truck_plate || p.moved_to_trip_code)"
                      :style="{color: '#195BFF', fontSize: '11px', display: 'block'}"
                      :title="$t('fleetTripDetail.moved_to_trip', { trip: p.moved_to_trip_code || p.moved_to_trip_id })">
                  → {{ $t("fleetTripDetail.truck") }} {{ p.moved_to_truck_plate || p.moved_to_trip_code }}
                </span>
              </td>
              <td :style="{color: '#8a909a'}">
                <span v-if="p.dest_prefix" class="dest-prefix">{{ p.dest_prefix }}</span>
                {{ p.dest_name || (p.dest_stop_id ? ('#' + p.dest_stop_id) : '—') }}
              </td>
              <td>
                {{ p.loaded_name || '—' }}
                <br><small :style="{color: 'grey'}">{{ FleetFormatTime(p.loaded_at) }}</small>
                <!-- The bag it rode aboard in; absent on a per-parcel scan. -->
                <template v-if="p.loaded_bag_number">
                  <br><small :style="{color: '#e8a10f'}"
                             :title="$t('fleetTripDetail.bag_scanned_title')">
                    <i class="fal fa-box"></i> {{ p.loaded_bag_number }}
                  </small>
                </template>
              </td>
              <td>
                <span v-if="p.delivered_name || p.delivered_at">
                  {{ p.delivered_name || '—' }}
                  <br><small :style="{color: 'grey'}">{{ FleetFormatTime(p.delivered_at) }}</small>
                </span>
                <span v-else :style="{color: p.on_truck ? 'crimson' : 'grey'}">
                  {{ p.on_truck ? $t('fleetTripDetail.still_on_truck') : '—' }}
                </span>
              </td>
              <td class="num">{{ p.weight_kg }}</td>
            </tr>
            </tbody>
          </table>
        </div>
          <Loading v-if="parcelListLoading"/>
          <p v-if="!parcelListLoading && parcelListLoaded && !parcelList.length"
             :style="{textAlign: 'center', color: 'grey', padding: '12px'}">
            {{ $t("fleetTripDetail.no_parcels_found") }}
          </p>
          <Pagination v-if="parcelPagination.last_page > 1"
                      :pagination="parcelPagination" :offset="parcelOffset"
                      @paginate="OnParcelPaginate"></Pagination>
        </div>
      </div>

      <div class="columns">
        <!-- Stops -->
        <div class="column is-7">
          <div class="box">
            <label class="label"><i class="fal fa-map-marker-alt"></i> {{ $t("fleetTripDetail.trip_stops") }}</label>
            <vs-table>
              <template #thead>
                <vs-tr>
                  <vs-th>#</vs-th>
                  <vs-th>{{ $t("fleetTripDetail.stop") }}</vs-th>
                  <vs-th>{{ $t("fleetTripDetail.planned_arrival") }}</vs-th>
                  <vs-th>{{ $t("fleetTripDetail.actual_arrival") }}</vs-th>
                  <vs-th>{{ $t("fleetTripDetail.actual_departure") }}</vs-th>
                  <vs-th>{{ $t("fleetTripDetail.status") }}</vs-th>
                  <vs-th></vs-th>
                </vs-tr>
              </template>
              <template #tbody>
                <vs-tr :key="index" :data="stop" v-for="(stop, index) in trip.stops">
                  <vs-td :style="{textAlign: 'center'}">{{ stop.sequence }}</vs-td>
                  <vs-td>
                    {{ StopName(stop) }}
                    <span v-if="IsOriginStop(stop)" class="origin-tag"
                          :title="$t('fleetTripDetail.origin_stop_title')">
                      <i class="fal fa-truck-loading"></i> {{ $t("fleetTripDetail.origin") }}
                    </span>
                    <span v-if="EtaStopBadge && liveEta.target_stop_id === stop.id" class="eta-tag"
                          :title="$t('fleetTripDetail.eta_title')">
                      <i class="fal fa-stopwatch"></i> {{ EtaStopBadge }}
                    </span>
                    <vs-tooltip v-if="stop.checkin_lat">
                      <i class="fal fa-map-pin" :style="{color: '#195BFF', marginLeft: '4px'}"></i>
                      <template #tooltip>GPS: {{ stop.checkin_lat }}, {{ stop.checkin_lng }}</template>
                    </vs-tooltip>
                    <!-- Proof-of-arrival photos the driver took at this stop.
                         The API has always carried them; nothing rendered them. -->
                    <a v-if="StopPhotos(stop).length" class="photo-tag" href="javascript:void(0)"
                       :title="$t('fleetTripDetail.view_stop_photos', { n: StopPhotos(stop).length })"
                       @click="OpenStopPhotos(stop)">
                      <i class="fal fa-camera"></i> {{ StopPhotos(stop).length }}
                    </a>
                  </vs-td>
                  <vs-td>{{ FleetFormatTime(stop.planned_arrival_at) }}</vs-td>
                  <vs-td>{{ FleetFormatTime(stop.checked_in_at) }}</vs-td>
                  <vs-td>{{ FleetFormatTime(stop.checked_out_at) }}</vs-td>
                  <vs-td>{{ FleetStopStatusLabel(stop.status) }}</vs-td>
                  <vs-td style="text-align: right; width: 60px">
                    <vs-button circle icon flat size="small" :title="$t('fleetTripDetail.reconcile_stop_title')"
                               @click="OpenReconciliation(stop)">
                      <i class="fal fa-tasks"></i>
                    </vs-button>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </div>

          <!-- Fuel / oil the driver recorded (ນ້ຳມັນ). Captured in the app only;
               this is where it is read back. Self-hides on an old backend. -->
          <div class="box" v-if="tripFuelLogs.length">
            <label class="label">
              <i class="fal fa-gas-pump"></i> {{ $t("fleetTripDetail.fuel_logs_title") }}
              <span class="fuel-total">
                {{ FleetFormatNumber(tripFuelTotals.liters, 1) }} {{ $t("fleetTripDetail.liters_unit") }}
                <template v-if="CanViewCost">
                  · {{ FleetFormatNumber(tripFuelTotals.amount, 0) }} {{ $t("common.ກີບ") }}
                </template>
              </span>
            </label>
            <!-- Litres/station/time stay for everyone — that is how a planner
                 checks a refuel against the route standard. The amount and the
                 receipt PHOTO go together behind the fleet-cost permission: a
                 receipt is a picture of the amount, so withholding the number
                 while showing a scan of it would gate nothing. -->
            <table class="mini-table">
              <thead>
              <tr>
                <th>{{ $t("fleetTripDetail.time") }}</th>
                <th>{{ $t("fleetTripDetail.liters_unit") }}</th>
                <th v-if="CanViewCost">{{ $t("fleetTripDetail.amount") }}</th>
                <th>{{ $t("fleetTripDetail.station") }}</th>
                <th v-if="CanViewCost">{{ $t("fleetTripDetail.receipt") }}</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(log, i) in tripFuelLogs" :key="i">
                <td>{{ FleetFormatDateTime(log.entered_at) }}</td>
                <td>{{ FleetFormatNumber(log.liters, 1) }}</td>
                <td v-if="CanViewCost">{{ FleetFormatNumber(log.amount, 0) }}</td>
                <td>{{ log.station || '—' }}</td>
                <td v-if="CanViewCost">
                  <a v-if="log.receipt_image" href="javascript:void(0)"
                     @click="OpenFuelReceipt(log)">
                    <img :src="log.receipt_image" class="receipt-thumb" :alt="$t('fleetTripDetail.fuel_receipt')"/>
                  </a>
                  <span v-else :style="{color: '#b0b0b0'}">{{ $t("fleetTripDetail.no_photo") }}</span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Reconciliation tab (T029) -->
          <div class="box" v-if="reconciliationStop">
            <label class="label">
              <i class="fal fa-tasks"></i>
              {{ $t("fleetTripDetail.reconcile_stop") }}: {{ StopName(reconciliationStop) }}
              <vs-button circle icon flat size="small" :style="{float: 'right'}" @click="CloseReconciliation()">
                <i class="fal fa-times"></i>
              </vs-button>
              <vs-button circle icon flat size="small" :style="{float: 'right'}"
                         :loading="reconciliationLoading" :title="$t('fleetTripDetail.refresh_stop_title')"
                         @click="RefreshReconciliation()">
                <i class="fal fa-sync"></i>
              </vs-button>
              <span v-if="autoRefreshInterval > 0" class="recon-auto"
                    :title="$t('fleetTripDetail.recon_auto_title')">
                <i class="fal fa-sync-alt"></i> {{ $t("fleetTripDetail.auto") }}
              </span>
            </label>
            <div class="recon-search">
              <i class="fal fa-search"></i>
              <input type="text" v-model="reconciliationSearch" :placeholder="$t('fleetTripDetail.search_bill_placeholder')"
                     @keyup.enter="ApplyReconciliationSearch()"/>
              <vs-button flat size="small" @click="ApplyReconciliationSearch()">{{ $t("fleetTripDetail.search") }}</vs-button>
              <vs-button flat size="small" v-if="reconciliation && reconciliation.search"
                         @click="ClearReconciliationSearch()">{{ $t("fleetTripDetail.clear") }}
              </vs-button>
            </div>
            <!-- The full-panel spinner is for the FIRST open only. On a refresh
                 (or a search) the rows stay on screen and the header button
                 spins instead — swapping the whole table out for a spinner threw
                 away whatever the operator was reading mid-unload. -->
            <Loading v-if="reconciliationLoading && !reconciliation"/>
            <div v-if="reconciliation">
              <p :style="{fontSize: '13px'}" v-if="reconciliation.planned">
                {{ $t("fleetTripDetail.planned_drop_here") }}: {{ reconciliation.planned.bills }} {{ $t("fleetTripDetail.bills") }} / {{ reconciliation.planned.pieces }} {{ $t("fleetTripDetail.pieces") }} /
                {{ FleetFormatNumber(reconciliation.planned.weight_kg, 1) }} kg
              </p>
              <p v-if="reconciliation.search" class="recon-hint">
                <i class="fal fa-filter"></i> {{ $t("fleetTripDetail.recon_filter_hint", { search: reconciliation.search }) }}
              </p>
              <p v-if="reconciliation.truncated" class="recon-hint truncated">
                <i class="fal fa-exclamation-triangle"></i>
                {{ $t("fleetTripDetail.recon_truncated", { n: reconciliation.row_cap }) }}
              </p>

              <div v-for="(section, sIndex) in reconciliationSections" :key="sIndex">
                <p class="recon-section" v-if="section.rows.length" :style="{color: section.color}">
                  {{ section.label }} ({{ section.rows.length }}<template
                    v-if="section.total > section.rows.length"> {{ $t("fleetTripDetail.of_total") }} {{ section.total }}</template>)
                </p>
                <table class="mini-table" v-if="section.rows.length">
                  <thead>
                  <tr>
                    <th v-if="section.movable" :style="{width: '30px'}"></th>
                    <th>{{ $t("fleetTripDetail.bill_number") }}</th>
                    <th>kg</th>
                    <th>{{ $t("fleetTripDetail.status") }}</th>
                    <th>{{ $t("fleetTripDetail.unload_time") }}</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(row, rIndex) in section.rows" :key="rIndex">
                    <td v-if="section.movable">
                      <input type="checkbox" :value="row.parcel_in_shipment_id" v-model="moveParcelIds">
                    </td>
                    <td>{{ row.bill_number }}
                      <span v-if="row.is_off_route" :style="{color: '#e8a10f', fontSize: '11px'}" :title="$t('fleetTripDetail.off_route_parcel_title')">{{ $t("fleetTripDetail.off_route_paren") }}</span>
                    </td>
                    <td>{{ row.weight_kg }}</td>
                    <td>{{ FleetParcelStatusLabel(row.status) }}</td>
                    <td>{{ FleetFormatTime(row.delivered_at) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <!-- Move wrong-pick batch to another trip (FR-035) -->
              <div v-if="moveParcelIds.length" class="move-box">
                <label class="label">{{ $t("fleetTripDetail.move_selected", { n: moveParcelIds.length }) }}</label>
                <div class="columns">
                  <div class="column is-8">
                    <model-list-select
                        :list="moveTargetOptions"
                        v-model="moveTargetTripId"
                        :placeholder="$t('fleetTripDetail.select_target_trip')"
                        option-text="display_name"
                        option-value="id"
                    >
                    </model-list-select>
                  </div>
                  <div class="column is-4">
                    <vs-button flat :loading="btnLoading" :disabled="!moveTargetTripId" @click="SubmitMove()">
                      {{ $t("fleetTripDetail.move_parcels") }}
                    </vs-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Crew -->
        <div class="column is-5">
          <div class="box">
            <label class="label"><i class="fal fa-users"></i> {{ $t("fleetTripDetail.trip_drivers") }}</label>
            <vs-table>
              <template #thead>
                <vs-tr>
                  <vs-th>{{ $t("fleetTripDetail.driver") }}</vs-th>
                  <vs-th>{{ $t("fleetTripDetail.role") }}</vs-th>
                  <vs-th>{{ $t("fleetTripDetail.assigned") }}</vs-th>
                  <vs-th>{{ $t("fleetTripDetail.released") }}</vs-th>
                  <vs-th></vs-th>
                </vs-tr>
              </template>
              <template #tbody>
                <vs-tr :key="index" :data="crew" v-for="(crew, index) in trip.drivers">
                  <vs-td>
                    {{ crew.driver_name || DriverName(crew.driver_id) }}
                    <br>
                    <small>{{ $t("fleetTripDetail.driver_card_id") }}:
                      <b :style="{color: '#e21312'}">{{ crew.driver_card_id || DriverCardId(crew.driver_id) }}</b>
                    </small>
                  </vs-td>
                  <vs-td>{{ crew.driver_role === 'primary' ? $t('fleetTripDetail.primary_driver') : $t('fleetTripDetail.crew_driver') }}</vs-td>
                  <vs-td>{{ FleetFormatTime(crew.assigned_at) }}</vs-td>
                  <vs-td>
                    <span v-if="crew.released_at">{{ FleetFormatTime(crew.released_at) }}</span>
                    <span v-else :style="{color: '#2dcc70'}">{{ $t("fleetTripDetail.still_assigned") }}</span>
                  </vs-td>
                  <vs-td style="text-align: right; width: 50px">
                    <vs-button v-if="!crew.released_at" circle icon flat danger size="small"
                               :title="$t('fleetTripDetail.release_from_trip')" @click="ReleaseDriver(crew.id)">
                      <i class="fal fa-user-minus"></i>
                    </vs-button>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>

            <div :style="{marginTop: '15px'}" v-if="trip.status === 'planned' || trip.status === 'in_progress'">
              <label class="label">{{ $t("fleetTripDetail.add_driver") }}</label>
              <div class="columns">
                <div class="column is-7">
                  <model-list-select
                      :list="drivers"
                      v-model="addDriverId"
                      :placeholder="$t('fleetTripDetail.select_driver_placeholder')"
                      option-text="name"
                      option-value="id"
                  >
                  </model-list-select>
                </div>
                <div class="column is-3">
                  <div class="select" :style="{width: '100%'}">
                    <select class="input" v-model="addDriverRole">
                      <option value="crew">{{ $t("fleetTripDetail.crew_driver") }}</option>
                      <option value="primary">{{ $t("fleetTripDetail.primary_driver") }}</option>
                    </select>
                  </div>
                </div>
                <div class="column is-2">
                  <vs-button circle icon flat :disabled="!addDriverId" :loading="btnLoading" @click="AddDriver()">
                    <i class="fal fa-user-plus"></i>
                  </vs-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Problems the driver reported on this trip (FR-023) -->
      <div class="columns" v-if="trip.problems && trip.problems.length">
        <div class="column is-12">
          <div class="box">
            <label class="label">
              <i class="fal fa-exclamation-triangle" :style="{color: '#e8a10f'}"></i>
              {{ $t("fleetTripDetail.driver_problems") }} ({{ trip.problems.length }})
            </label>
            <div class="problem-item" v-for="(p, pIndex) in trip.problems" :key="p.id || pIndex">
              <div class="problem-head">
                <span class="problem-desc">{{ p.description }}</span>
                <span class="problem-time">{{ FleetFormatTime(p.created_at) }}</span>
              </div>
              <div class="problem-meta">
                <span v-if="p.trip_stop_id">
                  <i class="fal fa-map-marker-alt"></i> {{ ProblemStopName(p.trip_stop_id) }}
                </span>
                <span v-else>
                  <i class="fal fa-route"></i> {{ $t("fleetTripDetail.general_no_stop") }}
                </span>
                <span class="problem-reporter" v-if="p.reporter_name">
                  <i class="fal fa-user"></i> {{ p.reporter_name }}<template v-if="p.reporter_tel"> ({{ p.reporter_tel }})</template>
                </span>
                <span class="problem-reporter" v-else-if="p.reported_by">
                  <i class="fal fa-user"></i> {{ $t("fleetTripDetail.user_id") }}: {{ p.reported_by }}
                </span>
              </div>
              <div class="problem-images" v-if="p.images && p.images.length">
                <a v-for="(img, iIndex) in p.images" :key="iIndex" :href="img" target="_blank" rel="noopener">
                  <img :src="img" class="problem-thumb" :alt="$t('fleetTripDetail.problem_photo')">
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Close dialog (T035) -->
    <ModalClose>
      <template v-slot="{ close }">
        <div>
          <h4 class="not-margin"
              :style="{ fontWeight: 'bold', color: '#195BFF', fontSize: '18px', textAlign: 'left' }">
            <i class="fal fa-flag-checkered" :style="{ width: '25px', fontSize: '18px' }"></i>
            {{ $t("fleetTripDetail.close_trip") }} {{ trip ? trip.trip_code : '' }}
          </h4>
          <div class="field" :style="{marginTop: '20px'}">
            <div class="control">
              <label class="label">{{ $t("fleetTripDetail.start_odometer") }}</label>
              <input class="input" type="number" min="0" step="0.1" v-model="closeValue.start_odometer_km"
                     :placeholder="$t('fleetTripDetail.start_odometer_placeholder')">
            </div>
          </div>
          <div class="field">
            <div class="control">
              <label class="label">
                {{ $t("fleetTripDetail.end_odometer") }}
                <span class="text-danger" v-if="!closeValue.force">*</span>
                <span v-else :style="{color: 'grey', fontWeight: 'normal', fontSize: '12px'}">
                  {{ $t("fleetTripDetail.optional_on_force") }}
                </span>
              </label>
              <input class="input" type="number" min="0" step="0.1" v-model="closeValue.end_odometer_km">
            </div>
          </div>
          <div class="field">
            <div class="control">
              <label class="label">
                {{ $t("fleetTripDetail.remark") }} <span class="text-danger" v-if="closeValue.force">*</span>
              </label>
              <input class="input" v-model="closeValue.remark"
                     :placeholder="closeValue.force ? $t('fleetTripDetail.force_reason_placeholder') : $t('fleetTripDetail.remark_placeholder')">
            </div>
          </div>

          <!-- Leftover gate: a clean close needs 0 parcels on the truck. If any
               remain, only a manager may force-close (parcels stay attributed
               as leftover in the summary) — and a force needs a written reason. -->
          <div class="field" v-if="CloseOnTruckPieces > 0 || closeBlockedOnTruck > 0">
            <div class="conflict-box">
              <p :style="{fontWeight: 'bold', color: 'crimson', fontSize: '13px'}">
                <i class="fal fa-exclamation-triangle"></i>
                {{ $t("fleetTripDetail.parcels_still_on_truck", { n: closeBlockedOnTruck || CloseOnTruckPieces }) }}
              </p>
              <p :style="{fontSize: '12px', color: '#666', margin: '4px 0 8px'}">
                {{ $t("fleetTripDetail.force_close_hint") }}
              </p>
              <label :style="{display: 'flex', alignItems: 'center', fontSize: '13px', cursor: 'pointer'}">
                <input type="checkbox" v-model="closeValue.force" :style="{marginRight: '8px'}">
                {{ $t("fleetTripDetail.force_close_checkbox") }}
              </label>
            </div>
          </div>

          <div ref="target" id="target" class="center">
            <div class="con-btns">
              <vs-button ref="button" flat class="form-btn-loading" :loading="btnLoading"
                         @click="SubmitClose(close)">
                {{ closeValue.force ? $t('fleetTripDetail.force_close') : $t('fleetTripDetail.confirm_close') }}
              </vs-button>
            </div>
          </div>
        </div>
      </template>
    </ModalClose>

    <!-- Proof-of-arrival photo lightbox. Plain overlay rather than a vs-dialog:
         it only shows images and closes, and the backdrop click is the whole
         interaction. -->
    <div v-if="photoViewer.open" class="photo-viewer" @click.self="CloseStopPhotos()">
      <div class="photo-viewer-inner">
        <div class="photo-viewer-head">
          <span><i class="fal fa-camera"></i> {{ $t("fleetTripDetail.stop_photos") }}: {{ photoViewer.title }}</span>
          <a href="javascript:void(0)" @click="CloseStopPhotos()"><i class="fal fa-times"></i></a>
        </div>
        <div class="photo-viewer-body">
          <a v-for="(src, i) in photoViewer.photos" :key="i" :href="src" target="_blank" rel="noopener">
            <img :src="src" :alt="$t('fleetTripDetail.stop_photos')"/>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {ModelListSelect} from 'vue-search-select';
import Loading from '@views/Loading';
import Pagination from '@/Plugins/Paginations/Pagination';
import MixinFleetTrips from '@/Mixins/Fleet/Trips';
import MixinFleetRounds from '@/Mixins/Fleet/Rounds';
import {FLEET_ERROR_MESSAGES} from '@/Mixins/Fleet/FleetHelpers';
import FleetStopsMap from '@views/SuperAdmin/Fleet/FleetStopsMap';

export default {
  components: {
    ModelListSelect,
    Loading,
    Pagination,
    FleetStopsMap,
  },
  mixins: [MixinFleetTrips, MixinFleetRounds],
  data: () => ({
    // Auto-refresh, mirroring the sorting-mode pages. On by default at 5s: this
    // page is watched live while a trip runs, and the numbers it shows (stops,
    // ledger, parcels) move without any action taken here.
    autoRefreshInterval: 5000,
    autoRefreshTimer: null,
    autoRefreshInFlight: false,
    // Separate from autoRefreshInFlight: the manual button owns its own spinner
    // and must not be blocked by (or block) a timer tick already in flight.
    manualRefreshing: false,
    // US2: this trip's truck on the route map, refreshed with the page's own
    // auto-reload. Null whenever GPS is off, the trip is not running, or it has
    // never reported — the map then draws stops only, exactly as before.
    liveTruck: null,
    // US3: arrival estimate for the next expected stop, delivered by the SAME
    // poll as the position above (one request answers "where is it" and "when
    // does it get there"). Null on an old backend; a self-describing `state`
    // otherwise, so an untrustworthy estimate shows nothing rather than a
    // number dispatch would plan around.
    liveEta: null,
    showVolumePanel: false,
    showEditPanel: false,
    editTruckId: '',
    editRemoveTruck: false,
    editDepartureTime: '',
    reconciliationStop: null,
    // Bill-number filter for the stop reconciliation. Applied SERVER-side (a
    // main-line stop can hold 1000+ pieces), so it is a query, not a v-model
    // over an already-downloaded list.
    reconciliationSearch: '',
    // Lightbox for a stop's proof-of-arrival photos.
    photoViewer: {open: false, title: '', photos: []},
    addDriverId: '',
    addDriverRole: 'crew',
    moveTargetOptions: [],
    ledger: null,
    drill: {open: false, loading: false, rows: [], title: '', mode: 'movement'},
    // Searchable full parcel list on the trip (find a parcel by number).
    showParcelList: true, // collapsible; shown by default
    parcelSearch: '',
    parcelStatusFilter: '',
    // Scope the list to one bag (ຖົງທີ່ໂຫຼດຂຶ້ນລົດ drill). Number is display only.
    parcelBagId: null,
    parcelBagNumber: '',
    parcelList: [],
    parcelListTotal: null,
    parcelListLoading: false,
    parcelListLoaded: false,
    parcelPage: 1,
    parcelPagination: {current_page: 1, last_page: 1, total: 0},
    parcelOffset: 6, // page-number window for the Pagination component
  }),
  computed: {
    tripId() {
      return Number(this.$route.params.id);
    },
    // Facilities visited more than once on the route (return-leg loops) — their
    // ledger rows get a sequence tag so the two visits are distinguishable.
    repeatedFacilityKeys() {
      const counts = {};
      ((this.ledger && this.ledger.stops) || []).forEach((s) => {
        const key = `${s.stop_type}:${s.stop_id}`;
        counts[key] = (counts[key] || 0) + 1;
      });
      return counts;
    },
    displayCloseSummary() {
      return this.closeSummary || (this.trip && this.trip.close_summary) || null;
    },
    // GPS integrity block: the backend sends the live compute while the trip
    // is in progress and the persisted close-time snapshot afterwards. Absent
    // (old backend / GPS off / pre-feature trip) → the card self-hides.
    TripGpsIntegrity() {
      return (this.trip && this.trip.gps_integrity) || null;
    },
    TripGpsFlags() {
      return this.FleetGpsFlags(this.TripGpsIntegrity);
    },
    // Pieces PHYSICALLY still on the truck — drives the leftover warning +
    // force-close checkbox. Must include the carved-out off-route on-truck
    // parcels: they are physically aboard, so the backend close gate counts
    // them (FleetTripService), and this warning must match or a trip with only
    // an off-route leftover would show "0 on truck" yet reject a clean close.
    CloseOnTruckPieces() {
      if (!this.trip) return 0;
      const inRoute = Number(this.FleetTripTotals(this.trip).pieces) || 0;
      const totals = this.trip.totals || {};
      const offRoute = (totals.off_route && Number(totals.off_route.on_truck_pieces)) || 0;
      return inRoute + offRoute;
    },
    displayCloseWarnings() {
      if (this.closeWarnings && this.closeWarnings.length) return this.closeWarnings;
      const summary = this.displayCloseSummary;
      return (summary && summary.warnings) || [];
    },
    // Trip stops -> map pins (coords come from stopGeo, resolved off the
    // route detail; a stop without saved facility coords simply has no pin).
    tripMapStops() {
      if (!this.trip) return [];
      const ledgerByStop = {};
      ((this.ledger && this.ledger.stops) || []).forEach((s) => {
        ledgerByStop[s.trip_stop_id] = s;
      });
      return (this.trip.stops || []).map((stop) => {
        const geo = this.stopGeo[`${stop.stop_type}:${stop.stop_id}`] || {};
        const led = ledgerByStop[stop.id];
        const name = this.StopName(stop);
        const offRouteOnTruck = (led && led.off_route && led.off_route.on_truck) || 0;
        const offRouteLoaded = (led && led.off_route && led.off_route.loaded_here) || 0;
        const ledgerSummary = led
          ? {
            in: led.in.pieces,
            out: led.out.pieces,
            leaving: led.on_board_leaving.pieces,
            offRoute: offRouteOnTruck,
            offRouteLoaded,
          }
          : null;
        return {
          lat: geo.lat,
          lng: geo.lng,
          type: stop.stop_type,
          label: stop.sequence,
          name,
          title: led
            ? this.$t('fleetTripDetail.map_stop_title', {
              name, loaded: led.in.pieces, unloaded: led.out.pieces, leaving: led.on_board_leaving.pieces,
            })
              + (offRouteLoaded ? ` / ${this.$t('fleetTripDetail.off_route_plus', {n: offRouteLoaded})}` : '')
            : name,
          isStart: stop.stop_role === 'pickup' && stop.sequence === 1,
          tripStopId: stop.id,
          ledger: ledgerSummary,
        };
      }).filter((stop) => Number(stop.lat) && Number(stop.lng));
    },
    tripMapCheckins() {
      if (!this.trip) return [];
      return (this.trip.stops || [])
          .filter((stop) => Number(stop.checkin_lat) && Number(stop.checkin_lng))
          .map((stop) => ({
            lat: Number(stop.checkin_lat),
            lng: Number(stop.checkin_lng),
            title: `Check-in: ${this.StopName(stop)}`,
          }));
    },
    reconciliationSections() {
      const recon = this.reconciliation || {};
      // `counts` carries the TRUE size of each bucket for the current search;
      // `rows` may be capped. Showing both is what stops a capped list reading
      // as the whole set. Absent on an older backend → falls back to the rows.
      const counts = recon.counts || {};
      const section = (label, key, color, movable) => ({
        label,
        rows: recon[key] || [],
        total: counts[key] === undefined ? (recon[key] || []).length : counts[key],
        color,
        movable,
      });

      return [
        section(this.$t('fleetTripDetail.section_delivered_here'), 'delivered', '#2dcc70', false),
        section(this.$t('fleetTripDetail.still_on_truck'), 'still_on_truck', '#195BFF', true),
        section(this.$t('fleetTripDetail.section_carried_past'), 'carried_past', 'crimson', false),
        section(this.$t('fleetTripDetail.section_wrong_stop_intended_here'), 'wrong_stop_intended_here', '#e8a10f', true),
        section(this.$t('fleetTripDetail.section_wrongly_unloaded_here'), 'wrongly_unloaded_here', '#e8a10f', true),
      ];
    },
    // The live row + the plate to write on the marker. Kept as a computed so
    // the label appears as soon as the trip resolves: the position and the trip
    // detail are fetched in parallel, so the first live fix can land before the
    // plate is known.
    mapTruck() {
      if (!this.liveTruck) return null;
      const trip = this.trip || {};
      const plate = trip.truck_plate || '';
      const totals = trip.totals || {};
      const onTruck = totals.on_truck || {};
      const offRoute = totals.off_route || {};
      // The trip's OWN numbers, not the dispatch map's whole-truck aggregate:
      // this page carves off-route out of the status counters everywhere else,
      // so the bubble has to read the same way or the two would disagree.
      const stats = [
        {label: this.$t('fleetTripDetail.on_truck'), value: Number(onTruck.pieces) || 0, color: '#195BFF'},
        {label: this.$t('fleetTripDetail.delivered'), value: Number(totals.delivered_pieces) || 0, color: '#2dcc70'},
      ];
      if (Number(totals.wrong_stop_pieces)) {
        stats.push({label: this.$t('fleetTripDetail.wrong_stop'), value: Number(totals.wrong_stop_pieces), color: '#e2445c'});
      }
      const offRoutePieces = Number(offRoute.pieces) || 0;
      const offRouteOnTruck = Number(offRoute.on_truck_pieces) || 0;

      return {
        ...this.liveTruck,
        plate,
        title: `${plate} · ${trip.route_name || ''}`,
        direction: trip.direction,
        subtitle: this.$t('fleetTripDetail.map_truck_subtitle', {route: trip.route_name || '—', round: trip.round_number || '-'}),
        stats,
        note: offRoutePieces
          ? `${this.$t('fleetTripDetail.off_route_pieces', {n: offRoutePieces})}${offRouteOnTruck ? ` ${this.$t('fleetTripDetail.still_on_truck_paren', {n: offRouteOnTruck})}` : ''}`
          : null,
        seenText: this.LiveTruckSeen,
      };
    },
    // How long ago the truck last reported. A server-side snapshot, so it only
    // moves when the page reloads — which is the point of the auto-reload hint
    // beside it.
    LiveTruckSeen() {
      const s = Number(this.liveTruck && this.liveTruck.seconds_since) || 0;
      if (s < 60) return this.$t('fleetTripDetail.seconds_ago', {n: s});
      if (s < 3600) return this.$t('fleetTripDetail.minutes_ago', {n: Math.round(s / 60)});
      return this.$t('fleetTripDetail.hours_ago', {n: Math.round(s / 3600)});
    },
    // Fuel/oil entries on this trip. Absent on a backend that predates the
    // `fuel_logs` key, so the whole section hides rather than showing zeroes.
    tripFuelLogs() {
      const logs = this.trip && this.trip.fuel_logs;
      return Array.isArray(logs) ? logs : [];
    },
    tripFuelTotals() {
      return this.tripFuelLogs.reduce((acc, log) => ({
        liters: acc.liters + (Number(log.liters) || 0),
        amount: acc.amount + (Number(log.amount) || 0),
      }), {liters: 0, amount: 0});
    },
    // The stop the estimate is aimed at (the next one not yet departed).
    EtaTargetStop() {
      const id = this.liveEta && this.liveEta.target_stop_id;
      if (!id) return null;
      return ((this.trip && this.trip.stops) || []).find((s) => s.id === id) || null;
    },
    // Only `moving` carries a time; `stationary` still knows the distance, so
    // it says so instead of pretending. Every other state (stale, off-route,
    // no geometry, GPS off) renders NOTHING — a stale ETA is worse than none,
    // because dispatch would act on it.
    EtaNote() {
      const eta = this.liveEta;
      if (!eta || (eta.state !== 'moving' && eta.state !== 'stationary')) return null;

      const stop = this.EtaTargetStop;
      const name = stop ? this.StopName(stop) : this.$t('fleetTripDetail.next_stop');
      const km = (eta.remaining_km === null || eta.remaining_km === undefined)
        ? null : `${this.FleetFormatNumber(eta.remaining_km, 1)} km`;

      if (eta.state === 'stationary') {
        return km
          ? this.$t('fleetTripDetail.eta_stationary', {km, stop: name})
          : this.$t('fleetTripDetail.eta_stationary_unknown');
      }

      const at = this.FleetFormatTime(eta.eta_at);
      return this.$t('fleetTripDetail.eta_moving', {stop: name, minutes: eta.eta_minutes})
        + (at ? ` ${this.$t('fleetTripDetail.eta_about', {time: at})}` : '')
        + (km ? ` · ${this.$t('fleetTripDetail.eta_remaining', {km})}` : '');
    },
    // Compact form of the same estimate, pinned to its row in the stops table.
    EtaStopBadge() {
      const eta = this.liveEta;
      if (!eta || eta.state !== 'moving' || !eta.eta_minutes) return null;

      return this.$t('fleetTripDetail.eta_badge', {n: eta.eta_minutes});
    },
    // A closed or cancelled trip is frozen — nothing it shows can change
    // again, so polling it is pure waste. Drives the spinner/paused badge and
    // short-circuits the tick itself.
    TripIsLive() {
      return !!this.trip && (this.trip.status === 'planned' || this.trip.status === 'in_progress');
    },
  },
  watch: {
    // Clicking "ໄປຖ້ຽວຂາກັບ / ຂາໄປ" navigates to the paired leg, which is the
    // SAME route component with a different :id param. Vue Router reuses this
    // instance and does NOT re-run created(), so without this watcher the URL
    // would change but the page keeps the old trip. Re-fetch on id change and
    // reset any panels/search carried over from the previous trip.
    tripId(newId, oldId) {
      if (!newId || newId === oldId) return;
      this.reconciliationStop = null;
      this.reconciliation = null;
      this.closeSummary = null;
      this.closeWarnings = [];
      this.showEditPanel = false;
      this.showVolumePanel = false;
      // The other leg has its own truck (or none) — leaving this set would show
      // the previous leg's position on the new leg's map until the fetch lands.
      // Same for the ETA, whose target stop id belongs to the leg we are leaving.
      this.liveTruck = null;
      this.liveEta = null;
      this.drill = {open: false, loading: false, rows: [], title: '', mode: 'movement'};
      this.parcelSearch = '';
      this.parcelStatusFilter = '';
      // A bag belongs to ONE trip. The paired-leg link reuses this component,
      // so carrying the filter over would ask the new trip for the old trip's
      // bag — an empty list under a stale bag pill, reading as "this leg has no
      // parcels" rather than "wrong filter".
      this.parcelBagId = null;
      this.parcelBagNumber = '';
      this.parcelList = [];
      this.parcelListTotal = null;
      this.parcelListLoaded = false;
      this.parcelPage = 1;
      this.parcelPagination = {current_page: 1, last_page: 1, total: 0};
      this.ReloadTrip();
    },
  },
  methods: {
    WarningLabel(code) {
      return FLEET_ERROR_MESSAGES[code] || code;
    },
    /** Resolve a problem's trip_stop_id to its stop name via the loaded stops. */
    ProblemStopName(tripStopId) {
      const stop = (this.trip.stops || []).find((s) => s.id === tripStopId);
      return stop ? this.StopName(stop) : this.$t('fleetTripDetail.stop_hash', {id: tripStopId});
    },
    /** The route origin: stop #1 stamped stop_role='pickup' — parcels are
     * loaded/outbounded onto the truck here, never a delivery destination. */
    IsOriginStop(stop) {
      return stop && stop.stop_role === 'pickup' && stop.sequence === 1;
    },
    NullableKm(value) {
      if (value === null || value === undefined) return '...';
      return `${this.FleetFormatNumber(value, 1)} km`;
    },
    DistanceConfidenceLabel(confidence) {
      const map = {
        high: this.$t('fleetTripDetail.confidence_high'),
        low: this.$t('fleetTripDetail.confidence_low'),
        none: this.$t('fleetTripDetail.confidence_none'),
      };
      return map[confidence] || confidence || '...';
    },
    DistanceConfidenceColor(confidence) {
      const map = {high: '#2dcc70', low: '#e8a10f', none: '#9aa0a6'};
      return map[confidence] || '#9aa0a6';
    },
    ReloadTrip() {
      this.FetchTripDetail(this.tripId).catch(() => {
      });
      this.FetchLedger();
      this.FetchTripParcelList();
      this.FetchLiveTruck();
    },
    /**
     * Where this trip's truck is right now. One primary-key lookup, so it is
     * cheap enough to ride along with the 5s auto-reload. Any failure (old
     * backend, no permission) simply leaves the map without a truck — this is
     * a display extra, never a reason to break the page.
     */
    FetchLiveTruck() {
      return this.FetchTripLivePosition(this.tripId).then((res) => {
        this.liveTruck = (res.data && res.data.data) || null;
        // Absent on a backend that predates the ETA — the note simply hides.
        this.liveEta = (res.data && res.data.eta) || null;
      }).catch(() => {
        this.liveTruck = null;
        this.liveEta = null;
      });
    },
    /**
     * Background refresh: same three calls as ReloadTrip, but silent — no
     * spinner swap, no error toast. Skipped entirely while an edit form is
     * open (a reload underneath someone typing is hostile) and once the trip
     * is frozen.
     */
    /**
     * Everything the page keeps live, as one SILENT batch — no spinners, no
     * error toasts, nothing swapped out from under the operator. Shared by the
     * auto-refresh timer and the manual button so the two can never drift.
     */
    RefreshJobs() {
      const jobs = [
        this.FetchTripDetail(this.tripId, true).catch(() => {
        }),
        this.FetchLedger(),
        this.FetchTripParcelList(),
        this.FetchLiveTruck(),
      ];
      // The open reconciliation panel rides the same batch. It is the one view
      // an operator actually watches while a truck is being unloaded, so
      // leaving it out meant the page around it refreshed while the numbers
      // they were reading went stale. Silent + current search, so a refresh
      // never blanks the table or drops the filter.
      if (this.reconciliationStop) {
        jobs.push(this.FetchStopReconciliation(
          this.tripId, this.reconciliationStop.id, this.reconciliationSearch, true
        ));
      }

      return jobs;
    },
    SilentReload() {
      if (this.autoRefreshInFlight || this.showEditPanel || !this.TripIsLive) return;
      this.autoRefreshInFlight = true;
      const done = () => {
        this.autoRefreshInFlight = false;
      };
      Promise.all(this.RefreshJobs()).then(done).catch(done);
    },
    /**
     * Manual pull. Unlike the timer it ALSO works on a closed/cancelled trip —
     * the operator asked for it, and a frozen trip can still have had its
     * reconciliation amended. Still refuses while the edit form is open, where
     * a reload would clobber what they are typing.
     */
    ManualRefresh() {
      if (this.manualRefreshing) return;
      if (this.showEditPanel) {
        this.$notification.warning({message: this.$t('fleetTripDetail.close_edit_first')});
        return;
      }
      this.manualRefreshing = true;
      const done = () => {
        this.manualRefreshing = false;
      };
      Promise.all(this.RefreshJobs()).then(done).catch(done);
    },
    SetAutoRefresh(interval) {
      this.ClearAutoRefresh();
      this.autoRefreshInterval = interval;
      if (interval > 0) {
        this.autoRefreshTimer = setInterval(this.SilentReload, interval);
      }
    },
    ClearAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
    },
    // A new search / filter always restarts from page 1.
    SearchTripParcels() {
      this.parcelPage = 1;
      this.FetchTripParcelList();
    },
    // ຖົງທີ່ໂຫຼດຂຶ້ນລົດ → scope the parcel list to that bag's pieces. Expands the
    // list (it is collapsible) so the drill is visible without a second click.
    ShowBagParcels(bag) {
      this.parcelBagId = bag.bag_id;
      this.parcelBagNumber = bag.bag_number || '';
      this.showParcelList = true;
      this.SearchTripParcels();
    },
    ClearBagFilter() {
      this.parcelBagId = null;
      this.parcelBagNumber = '';
      this.SearchTripParcels();
    },
    // Pagination component → jump to a page (it already mutated current_page).
    OnParcelPaginate(page) {
      this.parcelPage = page;
      this.FetchTripParcelList();
    },
    // Load / search the full parcel list on this trip, 15 per page (keeps the
    // current search + status filter + page, so a reload after a move/close
    // re-runs the same view).
    FetchTripParcelList() {
      this.parcelListLoading = true;
      const params = {per_page: 15, page: this.parcelPage};
      const search = (this.parcelSearch || '').trim();
      if (search) params.search = search;
      if (this.parcelStatusFilter) params.status = this.parcelStatusFilter;
      if (this.parcelBagId) params.bag_id = this.parcelBagId;
      this.FetchTripParcels(this.tripId, params).then((res) => {
        const body = res.data || {};
        // Laravel paginator serializes flat: {data, current_page, last_page, total}.
        this.parcelList = body.data || [];
        this.parcelListTotal = body.total !== undefined ? body.total : this.parcelList.length;
        this.parcelPagination = {
          current_page: body.current_page || 1,
          last_page: body.last_page || 1,
          total: body.total || this.parcelList.length,
        };
        this.parcelPage = this.parcelPagination.current_page;
        this.parcelListLoading = false;
        this.parcelListLoaded = true;
      }).catch(() => {
        // Old backend (404) → hide the list quietly.
        this.parcelList = [];
        this.parcelListTotal = null;
        this.parcelPagination = {current_page: 1, last_page: 1, total: 0};
        this.parcelListLoading = false;
        this.parcelListLoaded = true;
      });
    },
    StartTrip() {
      this.TripStatusAction(this.tripId, 'start').then(() => this.ReloadTrip()).catch(() => {
      });
    },
    CancelTrip() {
      this.TripStatusAction(this.tripId, 'cancel').then(() => this.ReloadTrip()).catch(() => {
      });
    },
    // Create the return (ຂາກັບ) leg for a legacy outbound with no pair, then
    // reload so the pair link appears.
    MakeReturnTrip() {
      this.CreateReturnTrip(this.tripId).then(() => this.ReloadTrip()).catch(() => {
      });
    },
    ReopenTrip() {
      this.TripStatusAction(this.tripId, 'reopen').then(() => {
        this.closeSummary = null;
        this.closeWarnings = [];
        this.ReloadTrip();
      }).catch(() => {
      });
    },
    OpenCloseDialog() {
      this.closeBlockedOnTruck = 0;
      this.closeValue = {
        end_odometer_km: '',
        start_odometer_km: this.trip.start_odometer_km !== null ? this.trip.start_odometer_km : '',
        remark: this.trip.remark || '',
        force: false,
      };
      this.$store.commit('modalClose_State', true);
    },
    SubmitClose(close) {
      // A normal close needs the final odometer; a FORCE close swaps that
      // requirement for a written reason (mirrors the API contract).
      if (!this.closeValue.force
          && (this.closeValue.end_odometer_km === '' || this.closeValue.end_odometer_km === null)) {
        this.$notification.error({message: this.$t('fleetTripDetail.enter_end_odometer')});
        return;
      }
      if (this.closeValue.force && !String(this.closeValue.remark || '').trim()) {
        this.$notification.error({message: this.$t('fleetTripDetail.force_close_needs_reason')});
        return;
      }
      this.CloseTrip(this.tripId).then(() => {
        close();
        this.ReloadTrip();
      }).catch(() => {
      });
    },
    ToggleEditPanel() {
      if (this.showEditPanel) {
        this.showEditPanel = false;
        return;
      }
      if (!this.trucks.length) {
        this.FetchActiveTrucks();
      }
      this.editTruckId = this.trip.truck_id || '';
      this.editRemoveTruck = false;
      this.editDepartureTime = this.trip.planned_departure_at
          ? this.moment(String(this.trip.planned_departure_at)).format('HH:mm')
          : '';
      this.showEditPanel = true;
    },
    SubmitEditTrip() {
      const payload = {};
      if (this.editRemoveTruck) {
        payload.truck_id = null; // explicit null removes it (omitted keeps it)
      } else if (this.editTruckId) {
        payload.truck_id = this.editTruckId;
      }
      if (this.editDepartureTime) {
        payload.planned_departure_at = `${this.trip.service_date} ${this.editDepartureTime}:00`;
      }
      this.UpdateTrip(this.tripId, payload).then(() => {
        this.showEditPanel = false;
        this.ReloadTrip();
      }).catch(() => {
      });
    },
    ToggleVolumePanel() {
      if (this.showVolumePanel) {
        this.showVolumePanel = false;
        return;
      }
      this.FetchVolumeRecommendation(this.tripId).then(() => {
        this.showVolumePanel = true;
      });
    },
    /** ---------------- Stop-load ledger ---------------- */
    FetchLedger() {
      this.FetchStopLoadLedger(this.tripId).then((res) => {
        this.ledger = (res.data && res.data.data) || null;
      }).catch(() => {
        // Old backend (404) → the ledger card simply stays hidden.
        this.ledger = null;
      });
    },
    isRepeatedFacility(row) {
      return (this.repeatedFacilityKeys[`${row.stop_type}:${row.stop_id}`] || 0) > 1;
    },
    LedgerStateLabel(state) {
      return {
        open: this.$t('fleetTripDetail.ledger_open'),
        balanced: this.$t('fleetTripDetail.ledger_balanced'),
        force_closed_with_residual: this.$t('fleetTripDetail.ledger_force_closed'),
        cancelled: this.$t('fleetTripDetail.ledger_cancelled'),
        integrity_error: this.$t('fleetTripDetail.ledger_integrity_error'),
      }[state] || state || '...';
    },
    LedgerStateIcon(state) {
      return {
        balanced: 'fal fa-check-circle',
        force_closed_with_residual: 'fal fa-exclamation-triangle',
        cancelled: 'fal fa-ban',
        integrity_error: 'fal fa-exclamation-circle',
      }[state] || 'fal fa-truck-loading';
    },
    // Which truck(s) a stop's hand-offs went onto. Without this the ledger's
    // "ຍ້າຍ N" is a dead end for anyone tracing where a parcel actually went.
    TransferredToPlates(out) {
      const list = (out && out.transferred_to) || [];
      const labels = list
          .map((t) => t.truck_plate || t.trip_code)
          .filter((label) => !!label);
      return labels.length ? labels.join(', ') : '';
    },
    TransferredToTitle(out) {
      const list = (out && out.transferred_to) || [];
      if (!list.length) return this.$t('fleetTripDetail.transferred_other_truck');
      return this.$t('fleetTripDetail.transferred_to') + ': ' + list
          .map((t) => `${t.truck_plate || '—'} (${t.trip_code || t.trip_id})`)
          .join(', ');
    },
    MovementReasonLabel(reason) {
      return {
        correct: this.$t('fleetTripDetail.correct_stop'),
        delivered: this.$t('fleetTripDetail.delivered'),
        wrong_stop: this.$t('fleetTripDetail.wrong_stop'),
        transferred: this.$t('fleetTripDetail.transferred_other_truck'),
        removed: this.$t('fleetTripDetail.cancelled'),
        on_truck: this.$t('fleetTripDetail.on_truck'),
      }[reason] || reason || '';
    },
    OpenDrill(row, direction) {
      const titles = {
        in: this.$t('fleetTripDetail.drill_in_title', {stop: row.stop_name || row.stop_id}),
        out: this.$t('fleetTripDetail.drill_out_title', {stop: row.stop_name || row.stop_id}),
        on_board_leaving: this.$t('fleetTripDetail.drill_leaving_title', {stop: row.stop_name || row.stop_id}),
      };
      this.drill = {open: true, loading: true, rows: [], title: titles[direction] || '', mode: 'movement'};
      this.FetchStopMovements(this.tripId, row.trip_stop_id, {direction, per_page: 200}).then((res) => {
        this.drill.rows = (res.data && res.data.data) || [];
        this.drill.loading = false;
      }).catch(() => {
        this.drill.loading = false;
      });
    },
    // The carved-out off-route bucket, expanded: WHERE each mis-loaded parcel
    // was meant to go, where it was loaded, and whether it already dropped off.
    OpenOffRouteDrill() {
      this.drill = {open: true, loading: true, rows: [], title: this.$t('fleetTripDetail.off_route_parcels'), mode: 'off_route'};
      this.FetchOffRouteParcels(this.tripId).then((res) => {
        this.drill.rows = ((res.data && res.data.data) || {}).parcels || [];
        this.drill.loading = false;
      }).catch(() => {
        this.drill.loading = false;
      });
    },
    CloseDrill() {
      this.drill = {open: false, loading: false, rows: [], title: '', mode: 'movement'};
    },
    // A map marker's IN/OUT/leaving link → open the same drill panel.
    // The off-route link opens the trip-wide off-route bucket instead.
    OnMapDrill(payload) {
      if (payload.direction === 'off_route') {
        this.OpenOffRouteDrill();
        return;
      }
      const row = ((this.ledger && this.ledger.stops) || [])
          .find((s) => s.trip_stop_id === payload.tripStopId);
      if (row) this.OpenDrill(row, payload.direction);
    },
    OpenReconciliation(stop) {
      this.reconciliationStop = stop;
      this.moveParcelIds = [];
      this.moveTargetTripId = '';
      this.reconciliationSearch = '';
      this.FetchStopReconciliation(this.tripId, stop.id).catch(() => {
      });
      this.FetchMoveTargets();
    },
    // Re-queries the stop with the bill filter. Selections are dropped: they
    // refer to rows the next result may not contain, and a move must never act
    // on a parcel the operator can no longer see.
    ApplyReconciliationSearch() {
      if (!this.reconciliationStop) return;
      this.moveParcelIds = [];
      this.FetchStopReconciliation(this.tripId, this.reconciliationStop.id, this.reconciliationSearch)
        .catch(() => {
        });
    },
    ClearReconciliationSearch() {
      this.reconciliationSearch = '';
      this.ApplyReconciliationSearch();
    },
    // Manual re-pull of the open panel, keeping the current search. Not silent:
    // the operator asked for it, so they get the spinner and any error.
    RefreshReconciliation() {
      if (!this.reconciliationStop) return;
      this.FetchStopReconciliation(this.tripId, this.reconciliationStop.id, this.reconciliationSearch)
        .catch(() => {
        });
    },
    // Proof-of-arrival photos for a stop, defensive about shape: an older
    // backend sent raw paths, and a stop with none sends null.
    StopPhotos(stop) {
      const photos = stop && stop.arrival_photos;
      return Array.isArray(photos) ? photos.filter((p) => !!p) : [];
    },
    OpenStopPhotos(stop) {
      this.photoViewer = {open: true, title: this.StopName(stop), photos: this.StopPhotos(stop)};
    },
    // Fuel receipts reuse the stop-photo lightbox — same job, one viewer.
    OpenFuelReceipt(log) {
      if (!log || !log.receipt_image) return;
      const title = this.$t('fleetTripDetail.fuel_receipt_title', {liters: this.FleetFormatNumber(log.liters, 1)})
        + (log.station ? ` · ${log.station}` : '');
      this.photoViewer = {open: true, title, photos: [log.receipt_image]};
    },
    CloseStopPhotos() {
      this.photoViewer = {open: false, title: '', photos: []};
    },
    CloseReconciliation() {
      this.reconciliationStop = null;
      this.reconciliation = null;
      this.reconciliationSearch = '';
      this.moveParcelIds = [];
    },
    FetchMoveTargets() {
      // Open trips on the same service date, excluding this one.
      this.$http.get('v1/fleet/trips', {
        params: {date: this.trip.service_date, per_page: 50},
      }).then((res) => {
        this.moveTargetOptions = (res.data.data || [])
            .filter((row) => row.id !== this.tripId
                && (row.status === 'planned' || row.status === 'in_progress'))
            .map((row) => ({
              ...row,
              display_name: this.$t('fleetTripDetail.move_target_display', {code: row.trip_code, route: row.route_name, round: row.round_number}),
            }));
      }).catch(() => {
      });
    },
    SubmitMove() {
      this.MoveParcels(this.tripId).then(() => {
        this.moveParcelIds = [];
        this.moveTargetTripId = '';
        if (this.reconciliationStop) {
          this.FetchStopReconciliation(this.tripId, this.reconciliationStop.id).catch(() => {
          });
        }
        this.ReloadTrip();
      }).catch(() => {
      });
    },
    AddDriver() {
      this.AddTripDriver(this.tripId, this.addDriverId, this.addDriverRole).then(() => {
        this.addDriverId = '';
        this.ReloadTrip();
      }).catch(() => {
      });
    },
    ReleaseDriver(tripDriverId) {
      this.ReleaseTripDriver(this.tripId, tripDriverId).then(() => {
        this.ReloadTrip();
      }).catch(() => {
      });
    },
  },
  created() {
    this.FetchDrivers();
    this.ReloadTrip();
    this.SetAutoRefresh(this.autoRefreshInterval);
  },
  // Without this the interval outlives the page and keeps polling a trip
  // nobody is looking at, forever.
  beforeDestroy() {
    this.ClearAutoRefresh();
  },
};
</script>

<style lang="scss" scoped>
.no-truck-chip {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  background: #fff4e0;
  color: #b26a00;
  font-size: 12px;
}
/* Live-truck note in the map legend — red while the truck is reporting, grey
   once contact drops, matching the marker colours on the dispatch map. */
.live-truck-note {
  margin-left: 10px;
  color: #e53935;

  i {
    margin-right: 3px;
  }

  &.stale {
    color: #9aa0a6;
  }
}

/* Arrival estimate under the map legend. Blue while it is a real countdown,
   grey once the truck is only sitting still with a distance to show. */
.eta-note {
  margin-top: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #195BFF;

  i {
    margin-right: 4px;
  }

  &.stationary {
    color: #8a8f98;
    font-weight: 500;
  }
}

/* Bill-number filter above the stop reconciliation. */
.recon-search {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;

  > i {
    color: #909090;
  }

  input {
    flex: 1;
    min-width: 0;
    padding: 5px 9px;
    border: 1px solid #ededed;
    border-radius: 4px;
    font-size: 13px;
    // Form controls do NOT inherit font-family from the page, so without this
    // the Lao placeholder falls back to the browser default, which has no Lao
    // glyphs. $font carries BoonBaan.
    font-family: $font;

    &::placeholder {
      font-family: $font;
      color: #b0b0b0;
    }
  }
}

/* "auto-refreshing" marker in the reconciliation header — floated beside the
   manual refresh so it is clear the panel is live, not a frozen snapshot. */
.recon-auto {
  float: right;
  margin: 4px 6px 0 0;
  font-size: 11px;
  font-weight: 600;
  color: #2dcc70;

  i {
    margin-right: 3px;
  }
}

.recon-hint {
  font-size: 12px;
  color: #8a8f98;
  margin-bottom: 4px;

  &.truncated {
    color: #e8a10f;
    font-weight: 600;
  }

  i {
    margin-right: 4px;
  }
}

/* Province prefix (VTE, LPB, …) in front of a destination branch name — the
   village names alone repeat across provinces. */
.dest-prefix {
  display: inline-block;
  margin-right: 4px;
  padding: 0 5px;
  border-radius: 3px;
  background: rgba(25, 91, 255, .1);
  color: #195BFF;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .3px;
}

/* Fuel section: running total beside the heading + receipt thumbnails. */
.fuel-total {
  float: right;
  font-size: 12px;
  font-weight: 600;
  color: #f57c00;
}

.receipt-thumb {
  width: 42px;
  height: 42px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ededed;
}

/* Arrival-photo count beside a stop name. */
.photo-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  background: rgba(138, 143, 152, .14);
  color: #616161;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  i {
    margin-right: 3px;
  }
}

.photo-viewer {
  position: fixed;
  inset: 0;
  z-index: 52000;
  background: rgba(0, 0, 0, .62);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.photo-viewer-inner {
  background: #fff;
  border-radius: 6px;
  max-width: 900px;
  width: 100%;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.photo-viewer-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #ededed;
  font-weight: 600;
  font-size: 14px;

  i {
    margin-right: 5px;
  }

  a {
    color: #909090;
  }
}

.photo-viewer-body {
  padding: 12px;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  img {
    max-width: 260px;
    max-height: 260px;
    border-radius: 4px;
    border: 1px solid #ededed;
  }
}

/* Same estimate, pinned to the target stop's row in the stops table. */
.eta-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  background: rgba(25, 91, 255, .12);
  color: #195BFF;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  i {
    margin-right: 3px;
  }
}

/* Same control as the sorting-mode pages, sized down to sit inline in the
   header beside ກັບຄືນ rather than in a filter bar. */
.auto-refresh-control {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  margin-right: 8px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  vertical-align: middle;

  .refresh-label {
    font-size: 12px;
    color: #666;
    white-space: nowrap;
  }

  .refresh-options {
    display: inline-flex;
    gap: 4px;

    .refresh-btn {
      padding: 3px 9px;
      border: 1px solid #ddd;
      background: white;
      border-radius: 4px;
      font-size: 12px;
      color: #666;
      cursor: pointer;
      transition: all 0.2s ease;
      font-family: "Open Sans", "BoonBaan", "Noto Sans Lao", sans-serif;

      &:hover {
        border-color: #f57c00;
        color: #f57c00;
      }

      &.active {
        background: #f57c00;
        border-color: #f57c00;
        color: white;
      }
    }
  }

  /* Manual "refresh now", sized to sit level with the interval buttons. Square
     so the fa-spin state rotates about its own centre (same reason as the
     indicator below). */
  .refresh-now {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 24px;
    padding: 0;
    border: 1px solid #ddd;
    background: white;
    border-radius: 4px;
    font-size: 12px;
    color: #666;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      border-color: #f57c00;
      color: #f57c00;
    }

    &:disabled {
      cursor: default;
      color: #f57c00;
    }

    i {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1em;
      height: 1em;
      line-height: 1;
      transform-origin: 50% 50%;
    }
  }

  .refresh-indicator {
    color: #f57c00;
    font-size: 14px;
    display: inline-flex;
    align-items: center;

    /* fa-spin rotates the <i> about ITS OWN box centre, and FontAwesome only
       gives the glyph `display:inline-block; line-height:1` — a box as wide as
       the glyph's advance width but a full em tall. Rotating that non-square
       box swings the icon around a point that is not its optical centre, so it
       orbits instead of spinning in place. Squaring the box and centring the
       glyph inside it puts the rotation origin where the eye expects it. */
    i {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1em;
      height: 1em;
      line-height: 1;
      transform-origin: 50% 50%;
    }
  }

  .refresh-paused {
    font-size: 11px;
    color: #9aa0a6;
    white-space: nowrap;
  }
}

.status-chip {
  display: inline-flex;
  align-items: center;
  color: white;
  border-radius: 12px;
  padding: 2px 12px 4px;
  font-size: 13px;
  font-weight: bold;
  line-height: 1.5;
  margin-left: 8px;
}

.dir-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 12px;
  padding: 2px 12px 4px;
  font-size: 13px;
  font-weight: bold;
  line-height: 1.5;
  margin-left: 8px;

  &.dir-outbound {
    background: rgba(25, 91, 255, 0.12);
    color: #195BFF;
  }

  &.dir-return {
    background: rgba(0, 170, 170, 0.14);
    color: #0a9;
  }
}

.paired-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 6px;
  padding: 6px 14px;
  border: 1px solid #0a9;
  border-radius: 6px;
  background: rgba(0, 170, 170, 0.08);
  color: #0a9;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background: #0a9;
    color: #fff;
  }
}

.meta-label {
  font-size: 12px;
  color: grey;
  margin-bottom: 2px;
}

.meta-value {
  font-size: 15px;
  font-weight: bold;
}

.action-row {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  border-top: 1px solid #f0f0f0;
  padding-top: 10px;
}

/* GPS integrity card (001-fleet-gps-tracking) */
.gps-live-chip {
  background: #e8a10f;
  color: white;
  border-radius: 10px;
  padding: 2px 10px;
  font-size: 11px;
  margin-left: 8px;
  vertical-align: middle;
}

.gps-flags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.gps-flag-chip {
  color: white;
  border-radius: 12px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: bold;
}

.gps-note {
  font-size: 11px;
  color: grey;
  margin-top: 4px;
}

.mini-table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;

  th, td {
    padding: 4px 6px;
    border-bottom: 1px solid #f0f0f0;
    text-align: left;
  }

  th {
    color: #666;
  }
}

.recon-section {
  font-weight: bold;
  font-size: 13px;
  margin-top: 12px;
}

.origin-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  background: rgba(45, 204, 112, .12);
  color: #2dcc70;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  i {
    margin-right: 3px;
  }
}

.recommend-chip {
  display: inline-block;
  background: #2dcc70;
  color: white;
  border-radius: 12px;
  padding: 3px 12px;
  font-size: 13px;
  font-weight: bold;

  &.none {
    background: crimson;
  }
}

.conflict-box {
  background: #fff8e6;
  border: 1px solid #f3d488;
  border-radius: 6px;
  padding: 10px;
  margin: 10px 0;
}

.move-box {
  background: #eef4ff;
  border: 1px solid #c4d7ff;
  border-radius: 6px;
  padding: 10px;
  margin-top: 12px;
}

.problem-item {
  border: 1px solid #f3d488;
  background: #fffdf6;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.problem-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.problem-desc {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
}

.problem-time {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
}

.problem-meta {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;

  i {
    margin-right: 3px;
  }
}

.problem-images {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.problem-thumb {
  width: 84px;
  height: 84px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #eee;
  cursor: pointer;
  transition: transform 0.1s ease;

  &:hover {
    transform: scale(1.03);
  }
}

.table-scroll {
  overflow-x: auto;
}

.recon-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 10px;
  background: #eef4ff;
  border: 1px solid #c4d7ff;

  i {
    margin-right: 2px;
  }

  .recon-warn {
    color: #e8a10f;
  }

  &.recon-balanced {
    background: #f1f9f1;
    border-color: #bfe3bf;
  }

  &.recon-force_closed_with_residual,
  &.recon-integrity_error {
    background: #fdf1ee;
    border-color: #f3c0b8;
  }

  &.recon-cancelled {
    background: #f4f5f7;
    border-color: #e0e2e6;
    color: #8a909a;
  }
}

.ledger-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    border-bottom: 1px solid #f0f1f4;
    padding: 7px 8px;
    text-align: left;
    white-space: nowrap;
  }

  th {
    color: #5a6472;
    font-weight: 600;
  }

  .num {
    text-align: right;
  }

  .leaving {
    background: #fafcff;
    font-weight: 600;
  }

  .clickable {
    cursor: pointer;

    &:hover {
      background: #eef4ff;
    }
  }
}

.drill-box {
  margin-top: 12px;
  border: 1px solid #e6e8ec;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fbfcfe;
}

.offroute-chip {
  color: #e8a10f;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline dotted;
}
.offroute-chip:hover {
  color: #cf8b00;
}

.drill-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 13px;
}

/* Bags loaded by a DC bag-outbound; click drills the parcel list to the bag. */
.bag-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.bag-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 14px;
  border: 1px solid rgba(232, 161, 15, .5);
  background: rgba(232, 161, 15, .08);
  color: #b8790a;
  font-size: 12px;
  cursor: pointer;
}
.bag-chip:hover {
  background: rgba(232, 161, 15, .16);
}
.bag-chip.is-done {
  border-color: rgba(45, 204, 112, .5);
  background: rgba(45, 204, 112, .08);
  color: #1f9c53;
}
.bag-chip .bag-number {
  font-weight: 700;
}
.bag-chip .bag-count {
  opacity: .85;
}

.bag-filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: 8px;
  padding: 2px 9px;
  border-radius: 12px;
  background: rgba(232, 161, 15, .14);
  color: #b8790a;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.bag-filter-pill:hover {
  background: rgba(232, 161, 15, .24);
}
</style>
